/**
 * Compact read formats for the WebMCP readers (SPEC §5 `get_session` and
 * `get_track`, §4.3 compact note format). Pure functions of the session and
 * the injected engine / controller state:
 *
 * - `formatSession()` produces the ≤ {@link SESSION_CHAR_BUDGET}-character
 *   summary: groove, mode, position, per-track counts split human/agent, the
 *   progression, recent human actions with `ago` strings, undo depth, any
 *   open audition and a one-line next-step `hint`. The budget is enforced by
 *   construction (short keys, flags only when set) and then by trimming the
 *   human log oldest-first, so a maxed-out session still fits.
 * - `formatTrack()` produces the ≤ {@link TRACK_CHAR_BUDGET}-character view of
 *   one track: `describeNotes` for melodic tracks (1-based bars passed
 *   straight through), per-lane 16-character strings for drums with empty
 *   lanes dropped and identical bars collapsed to `{ all }`.
 * - `sessionHint()` is the arrangement-state advice the agent reads first.
 */
import type { Controller, Engine } from '../app/controller';
import type { Store } from '../state/store-api';
import { describeNotes, keyName } from '../music/theory';
import { EMPTY_PATTERN, pattern16, type PatternHit } from '../music/patterns';
import { ago } from '../ui/dom';
import {
  BARS,
  DRUM_LANES,
  MELODIC_TRACK_IDS,
  STEPS_PER_BAR,
  type AppMode,
  type Author,
  type BassPattern,
  type ChordRhythm,
  type DrumLane,
  type MelodicTrackId,
  type PendingAudition,
  type Session,
  type StyleId,
  type TrackId,
} from '../types';
import type { GetTrackInput } from './schemas';

/** Character budget for a `get_session` result (SPEC §5). */
export const SESSION_CHAR_BUDGET = 700;
/** Character budget for a `get_track` result (SPEC §5). */
export const TRACK_CHAR_BUDGET = 1500;
/** Longest human-action detail echoed in `human.recent`. */
export const RECENT_DETAIL_CHARS = 32;
/** Longest undo label echoed in `undo.top`. */
export const UNDO_LABEL_CHARS = 40;

/** What `formatSession` needs: the store plus live engine / controller state. */
export interface FormatDeps {
  store: Store;
  engine: Pick<Engine, 'isPlaying' | 'position'>;
  controller: Pick<Controller, 'audioReady'>;
  /** Clock for the `ago` strings; defaults to `Date.now`. */
  now?: () => number;
}

/** Per-track flags shown only when set. */
interface TrackFlagsOut {
  muted?: true;
  solo?: true;
}

/** `get_session.tracks.drums`. */
export interface DrumSummary extends TrackFlagsOut {
  hits: number;
  human: number;
  agent: number;
}

/** `get_session.tracks.bass`. */
export interface BassSummary extends TrackFlagsOut {
  notes: number;
  human: number;
  agent: number;
  pattern: BassPattern | null;
}

/** `get_session.tracks.chords`. */
export interface ChordsSummary extends TrackFlagsOut {
  notes: number;
  human: number;
  agent: number;
  progression: string[] | null;
  rhythm: ChordRhythm | null;
}

/** `get_session.tracks.lead`. */
export interface LeadSummary extends TrackFlagsOut {
  notes: number;
  human: number;
  agent: number;
}

/** The `get_session` result (SPEC §5). */
export interface SessionSummary {
  key: string;
  tempo: number;
  swing: number;
  style: StyleId;
  mode: AppMode;
  playing: boolean;
  /** 1-based bar and step-within-bar. */
  position: { bar: number; step: number };
  audio: 'ready' | 'muted_until_user_taps';
  tracks: { drums: DrumSummary; bass: BassSummary; chords: ChordsSummary; lead: LeadSummary };
  human: {
    recent: { ago: string; action: string }[];
    selected: { track: TrackId; bar: number };
    last_recording?: { notes: number; ago: string };
  };
  undo: { depth: number; top: { by: Author; label: string } | null };
  audition: { id: string; chosen: string | null; awaiting: boolean } | null;
  hint: string;
}

/** The `get_track` result for a melodic track. */
export interface MelodicTrackView {
  track: 'bass' | 'chords' | 'lead';
  key: string;
  /** The 1-based bars included. */
  bars: number[];
  /** Compact `b1: 1@1 3@5x4 | b2: …` text; `xN` marks a note held N steps. */
  notes: string;
  /** How to read `notes` (the schema is frozen, so the legend travels with the result). */
  legend: string;
  human: number;
  agent: number;
  pattern?: BassPattern | null;
  progression?: string[] | null;
  rhythm?: ChordRhythm | null;
}

/** One drum lane in `get_track`: identical bars collapse to `{ all }`. */
export type LaneView = { all: string } | Record<string, string>;

/** The `get_track` result for the drum track. */
export interface DrumTrackView {
  track: 'drums';
  key: string;
  bars: number[];
  /** Only lanes with at least one hit in the requested bars. */
  lanes: Partial<Record<DrumLane, LaneView>>;
  human: number;
  agent: number;
}

/** Either `get_track` shape. */
export type TrackView = MelodicTrackView | DrumTrackView;

function countBy(items: ReadonlyArray<{ by: Author }>): { human: number; agent: number } {
  let human = 0;
  let agent = 0;
  for (const it of items) {
    if (it.by === 'human') human += 1;
    else agent += 1;
  }
  return { human, agent };
}

function flags(track: Session['tracks'][TrackId]): TrackFlagsOut {
  const out: TrackFlagsOut = {};
  if (track.muted) out.muted = true;
  if (track.solo) out.solo = true;
  return out;
}

function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * The next-step hint for `get_session` (SPEC §5 examples). Priority: an armed
 * recording, an open audition, an empty session, a melody without chords,
 * chords without bass, no drums, everything present.
 */
export function sessionHint(s: Session, mode: AppMode, audition: PendingAudition | null): string {
  if (mode === 'recording') return 'recording armed: writers return after stop_recording';
  if (audition?.awaiting || mode === 'auditioning') return 'audition open: wait for the human; audition.chosen shows the pick';
  const lead = s.tracks.lead.notes;
  const chords = s.tracks.chords.notes.length;
  const bass = s.tracks.bass.notes.length;
  const drums = s.tracks.drums.hits.length;
  if (lead.length === 0 && chords === 0 && bass === 0 && drums === 0) {
    return 'empty session: ask the human to press Play, then add_drums';
  }
  if (lead.length > 0 && chords === 0) {
    const human = lead.filter((n) => n.by === 'human').length;
    const who = human > 0 ? `${human} human notes` : `${lead.length} agent notes`;
    return `lead has ${who} and no chords: call suggest_chords, then add_chords, then add_bass`;
  }
  if (chords > 0 && bass === 0) return 'chords but no bass: call add_bass';
  if (drums === 0) return 'no drums yet: call add_drums';
  return 'all four tracks have content: ask what to change, audition options, or export_midi';
}

/**
 * Build the `get_session` summary. Always ≤ {@link SESSION_CHAR_BUDGET}
 * characters once serialised: the human log is trimmed oldest-first (never
 * below two entries) and the hint clipped as a last resort.
 */
export function formatSession(deps: FormatDeps): SessionSummary {
  const { store, engine, controller } = deps;
  const now = deps.now ?? Date.now;
  const s = store.get();
  const mode = store.mode();
  const audition = store.audition();
  const pos = engine.position();
  const stack = store.undoStack();
  const top = stack[0];
  const recording = store.lastRecording();
  const t = s.tracks;

  const summary: SessionSummary = {
    key: keyName(s.key),
    tempo: s.tempo,
    swing: s.swing,
    style: s.style,
    mode,
    playing: engine.isPlaying(),
    position: { bar: pos.bar + 1, step: pos.step + 1 },
    audio: controller.audioReady() ? 'ready' : 'muted_until_user_taps',
    tracks: {
      drums: { hits: t.drums.hits.length, ...countBy(t.drums.hits), ...flags(t.drums) },
      bass: { notes: t.bass.notes.length, ...countBy(t.bass.notes), pattern: s.bassPattern, ...flags(t.bass) },
      chords: {
        notes: t.chords.notes.length,
        ...countBy(t.chords.notes),
        progression: s.progression ? s.progression.map((slot) => slot.numeral) : null,
        rhythm: s.progressionRhythm,
        ...flags(t.chords),
      },
      lead: { notes: t.lead.notes.length, ...countBy(t.lead.notes), ...flags(t.lead) },
    },
    human: {
      recent: store.humanRecent().map((a) => ({ ago: ago(a.at, now()), action: clip(a.detail, RECENT_DETAIL_CHARS) })),
      selected: { track: s.ui.selectedTrack, bar: s.ui.selectedBar + 1 },
    },
    undo: { depth: stack.length, top: top ? { by: top.by, label: clip(top.label, UNDO_LABEL_CHARS) } : null },
    audition: audition ? { id: audition.id, chosen: audition.chosen, awaiting: audition.awaiting } : null,
    hint: sessionHint(s, mode, audition),
  };
  if (recording) summary.human.last_recording = { notes: recording.notes, ago: ago(recording.at, now()) };

  trimToBudget(summary);
  return summary;
}

/** Tighter text clips used when the summary is over budget. */
const TIGHT_DETAIL_CHARS = 20;
const TIGHT_LABEL_CHARS = 24;
/** The hint is shortened to no less than this before the human log is dropped. */
const HINT_MIN_CHARS = 40;

function clipWords(text: string, max: number): string {
  if (text.length <= max) return text;
  let cut = text.slice(0, Math.max(1, max - 1));
  const space = cut.lastIndexOf(' ');
  if (space > 8) cut = cut.slice(0, space);
  return `${cut.trimEnd()}…`;
}

/**
 * Degrade a summary in stages until it fits {@link SESSION_CHAR_BUDGET}.
 * Data about what the human did outlives advice: oldest human actions go
 * first (down to one), then the echoed texts are clipped tighter, then a
 * resolved audition that ended in no choice (the log already says "chose
 * neither"; a kept option stays so `audition.chosen` still shows the pick),
 * then the hint is shortened on a word boundary (never below
 * {@link HINT_MIN_CHARS}), then `last_recording`, then the last human action,
 * and as a final resort the hint shrinks further.
 */
function trimToBudget(summary: SessionSummary): void {
  const over = () => JSON.stringify(summary).length - SESSION_CHAR_BUDGET;
  while (over() > 0 && summary.human.recent.length > 1) summary.human.recent.pop();
  if (over() > 0) {
    for (const a of summary.human.recent) a.action = clip(a.action, TIGHT_DETAIL_CHARS);
    if (summary.undo.top) summary.undo.top.label = clip(summary.undo.top.label, TIGHT_LABEL_CHARS);
  }
  if (over() > 0 && summary.audition && !summary.audition.awaiting && summary.audition.chosen === null) summary.audition = null;
  if (over() > 0) summary.hint = clipWords(summary.hint, Math.max(HINT_MIN_CHARS, summary.hint.length - over()));
  if (over() > 0) delete summary.human.last_recording;
  if (over() > 0) summary.human.recent = [];
  const excess = over();
  if (excess > 0) summary.hint = clipWords(summary.hint, Math.max(12, summary.hint.length - excess));
}

/** Validate and sort the 1-based bars of a `get_track` / `set_drum_steps` call; omitted = all. */
export function normalizeBars(bars: readonly number[] | undefined): number[] {
  if (!bars || bars.length === 0) return Array.from({ length: BARS }, (_, i) => i + 1);
  return [...new Set(bars.filter((b) => Number.isInteger(b) && b >= 1 && b <= BARS))].sort((a, b) => a - b);
}

function inBars(step: number, bars: readonly number[]): boolean {
  return bars.includes(Math.floor(step / STEPS_PER_BAR) + 1);
}

/**
 * Per-lane description of drum hits for the given 1-based bars: lanes with no
 * hit in those bars are dropped; a lane whose requested bars are identical
 * collapses to `{ all }`, otherwise `{ b1, b3, … }` keyed by bar.
 */
export function describeLanes(hits: readonly PatternHit[], bars: readonly number[]): Partial<Record<DrumLane, LaneView>> {
  const out: Partial<Record<DrumLane, LaneView>> = {};
  for (const lane of DRUM_LANES) {
    const strings = bars.map((b) => pattern16(hits, lane, b - 1));
    if (strings.every((p) => p === EMPTY_PATTERN)) continue;
    const first = strings[0]!;
    if (strings.every((p) => p === first)) out[lane] = { all: first };
    else {
      const perBar: Record<string, string> = {};
      bars.forEach((b, i) => {
        perBar[`b${b}`] = strings[i]!;
      });
      out[lane] = perBar;
    }
  }
  return out;
}

/**
 * Build the `get_track` view. Melodic tracks use the compact degree@step
 * text (bars are 1-based both here and in `describeNotes`); drums use
 * {@link describeLanes}. Counts cover the requested bars only.
 */
/** Legend shipped with every melodic `get_track` result. */
export const TRACK_LEGEND = 'xN = held N steps';

export function formatTrack(s: Session, input: GetTrackInput & { track: 'drums' }): DrumTrackView;
export function formatTrack(s: Session, input: GetTrackInput & { track: MelodicTrackId }): MelodicTrackView;
export function formatTrack(s: Session, input: GetTrackInput): TrackView;
export function formatTrack(s: Session, input: GetTrackInput): TrackView {
  const bars = normalizeBars(input.bars);
  const key = keyName(s.key);
  if (input.track === 'drums') {
    const hits = s.tracks.drums.hits.filter((h) => inBars(h.step, bars));
    return { track: 'drums', key, bars, lanes: describeLanes(s.tracks.drums.hits, bars), ...countBy(hits) };
  }
  const track = MELODIC_TRACK_IDS.includes(input.track) ? input.track : 'lead';
  const notes = s.tracks[track].notes.filter((n) => inBars(n.step, bars));
  const view: MelodicTrackView = {
    track,
    key,
    bars,
    notes: describeNotes(s.tracks[track].notes, s.key, { bars, maxChars: TRACK_CHAR_BUDGET - 260, lengths: true }),
    legend: TRACK_LEGEND,
    ...countBy(notes),
  };
  if (track === 'bass') view.pattern = s.bassPattern;
  if (track === 'chords') {
    view.progression = s.progression ? s.progression.map((slot) => slot.numeral) : null;
    view.rhythm = s.progressionRhythm;
  }
  return view;
}
